"use client";

import { forwardRef, useMemo } from "react";
import { DisclosureGroup } from "react-aria-components";

import { cn } from "../../utils/cn";
import { Accordion } from "./Accordion";
import { AccordionContent } from "./AccordionContent";
import { AccordionHeader } from "./AccordionHeader";
import { accordionVariants } from "./accordionVariants";
import { AccordionProvider } from "./context";

import type { DisclosureGroupProps } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import type { ForwardRefComponentWithSubcomponents } from "../../utils/types";
import type { AccordionClassNames } from "./accordionVariants";
import type { AccordionContext } from "./context";

export interface AccordionGroupProps
  extends Omit<DisclosureGroupProps, "className" | "children" | "allowsMultipleExpanded">,
    VariantProps<typeof accordionVariants> {
  /** Whether multiple accordions can be expanded at once. Defaults to true. */
  allowsMultipleExpanded?: boolean;
  className?: string;
  classNames?: AccordionClassNames;
  children?: React.ReactNode;
}

// AccordionGroup - A collapsible content component for organizing information into expandable sections.
//
// Structure:
// - <AccordionGroup> - Container that manages expansion state
// - <AccordionGroup.Accordion> - Individual accordion item (requires unique `id`)
// - <AccordionGroup.Header> - Clickable header with optional tooltip
// - <AccordionGroup.Content> - Collapsible content panel (i.e the content that is hidden/shown)
//
// Built on React Aria Components Disclosure:
// https://react-spectrum.adobe.com/react-aria/DisclosureGroup.html
// https://react-spectrum.adobe.com/react-aria/Disclosure.html

export const AccordionGroup = forwardRef<HTMLDivElement, AccordionGroupProps>(
  (
    { size, className, classNames, allowsMultipleExpanded = true, children, ...groupProps },
    ref
  ) => {
    const slots = useMemo(() => accordionVariants({ size }), [size]);
    const context = useMemo<AccordionContext>(() => ({ slots, classNames }), [slots, classNames]);

    return (
      <AccordionProvider value={context}>
        <DisclosureGroup
          ref={ref}
          allowsMultipleExpanded={allowsMultipleExpanded}
          {...groupProps}
          className={cn(slots.group(), classNames?.group, className)}
        >
          {children}
        </DisclosureGroup>
      </AccordionProvider>
    );
  }
) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  {
    Accordion: typeof Accordion;
    Header: typeof AccordionHeader;
    Content: typeof AccordionContent;
  },
  AccordionGroupProps
>;
AccordionGroup.displayName = "AccordionGroup";
AccordionGroup.Accordion = Accordion;
AccordionGroup.Header = AccordionHeader;
AccordionGroup.Content = AccordionContent;

export default AccordionGroup;
export { Accordion, AccordionHeader, AccordionContent };
